import {
  ButtonHTMLAttributes,
  CSSProperties,
  KeyboardEvent,
  isValidElement,
  useCallback,
  useId,
  useState,
} from 'react';

import { ReactComponent as SelectDownArrowIcon } from '../../components/assets/icons/SelectDownArrowIcon.svg';
import { useLayerPosition } from '../../hooks/useLayerPosition';
import useOnClickOutside from '../../hooks/useOnClickOutside';
import {
  Dropdown,
  DropdownHeader,
  DropdownItem,
  DropdownLayer,
  DropdownList,
  DropdownSectionTitle,
} from '../Dropdown';
import { DEFAULT_SELECT_PROPS } from './const';
import { StyledSelect } from './StyledSelect';
import { SelectOption } from './types';
import { getSelectOptions } from './utils';

interface SelectProps<T extends string | number>
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onChange'> {
  value: T;
  options: T[] | SelectOption<T>[];
  onChange?: (option: SelectOption<T>) => void;
  selectedLabel?: string;
  width?: CSSProperties['width'];
  showOptionDescription?: boolean;
  optionsPlacement?: Parameters<typeof Dropdown>[0]['placement'];
  optionsOffsetY?: Parameters<typeof Dropdown>[0]['offsetY'];
  optionsMaxHeight?: Parameters<typeof Dropdown>[0]['maxHeight'];
  optionsMinWidth?: Parameters<typeof Dropdown>[0]['minWidth'];
  optionsZIndex?: Parameters<typeof Dropdown>[0]['zIndex'];
}

const Select = <T extends string | number>({
  value,
  options,
  onChange,
  selectedLabel = DEFAULT_SELECT_PROPS.selectedLabel,
  width,
  showOptionDescription,
  optionsPlacement = DEFAULT_SELECT_PROPS.optionsPlacement,
  optionsOffsetY = DEFAULT_SELECT_PROPS.optionsOffsetY,
  optionsMaxHeight = DEFAULT_SELECT_PROPS.optionsMaxHeight,
  optionsMinWidth,
  optionsZIndex,
  ...props
}: SelectProps<T>) => {
  const selectId = useId();
  const listboxId = `${selectId}-listbox`;
  const descriptionHeaderId = `${selectId}-description`;
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const { anchorRef, layerRef } = useLayerPosition<
    HTMLButtonElement,
    HTMLDivElement
  >({
    placement: optionsPlacement,
    offsetY: optionsOffsetY,
    isLayerVisible: isOptionsOpen,
  });
  // single active-option state — set by BOTH mouse hover and keyboard
  // navigation; referenced from the combobox button via aria-activedescendant
  // (DOM focus never leaves the button)
  const [activeIndex, setActiveIndex] = useState(-1);
  const selectOptions = getSelectOptions(options);
  const selectedOption = selectOptions.find((o) => o.value === value);
  const activeOption = activeIndex >= 0 ? selectOptions[activeIndex] : null;

  const getOptionId = (index: number) => `${selectId}-option-${index}`;

  const hideOptions = useCallback(() => {
    setIsOptionsOpen(false);
    setActiveIndex(-1);
  }, []);

  const showOptions = () => {
    // the selected option (else the first) becomes the active option
    const selectedIndex = selectOptions.findIndex((o) => o.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setIsOptionsOpen(true);
  };

  const toggleOptions = () => (isOptionsOpen ? hideOptions() : showOptions());

  const handleClickItem = useCallback(
    (option: SelectOption<T>) => () => {
      onChange?.(option);
      hideOptions();
    },
    [onChange, hideOptions]
  );

  const selectActiveOption = () => {
    if (activeOption) onChange?.(activeOption);
    hideOptions();
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (!isOptionsOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        // preventDefault: the native button click (Enter keydown / Space
        // keyup) would immediately toggle the layer closed again
        event.preventDefault();
        showOptions();
      }
      return;
    }

    // the keyboard index walks selectOptions (the data), so interleaved
    // section titles are skipped for free
    const lastIndex = selectOptions.length - 1;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setActiveIndex((prev) => Math.min(prev < 0 ? 0 : prev + 1, lastIndex));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setActiveIndex((prev) => Math.max(prev < 0 ? 0 : prev - 1, 0));
        break;
      case 'Home':
        event.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        event.preventDefault();
        setActiveIndex(lastIndex);
        break;
      case 'Enter':
      case ' ':
        // preventDefault: keep the native click from re-toggling the layer
        event.preventDefault();
        selectActiveOption();
        break;
      case 'Escape':
        event.preventDefault();
        hideOptions();
        break;
      case 'Tab':
        // no preventDefault — close and let focus move on
        hideOptions();
        break;
      default:
        break;
    }
  };

  useOnClickOutside([anchorRef, layerRef], () => hideOptions?.());

  return (
    <>
      <StyledSelect
        ref={anchorRef}
        role="combobox"
        type="button"
        style={{ width }}
        aria-controls={listboxId}
        aria-expanded={isOptionsOpen ? 'true' : 'false'}
        aria-activedescendant={
          isOptionsOpen && activeOption ? getOptionId(activeIndex) : undefined
        }
        onClick={toggleOptions}
        onKeyDown={handleKeyDown}
        {...props}
      >
        {selectedOption?.label}
        <SelectDownArrowIcon className="sup-select-down-arrow" />
      </StyledSelect>
      <DropdownLayer
        id={selectId}
        ref={layerRef}
        style={{
          maxHeight: optionsMaxHeight,
          width: width === '100%' ? anchorRef?.current?.clientWidth : width,
          minWidth: optionsMinWidth,
          zIndex: optionsZIndex,
        }}
        isOpen={isOptionsOpen}
      >
        {showOptionDescription && (
          <DropdownHeader id={descriptionHeaderId}>
            {/* branch on the active option first — an active option with no
                description yields an empty header, never a neighbour's
                description mis-attributed through a cross-entity ?? fallback */}
            {activeOption
              ? activeOption.description
              : selectedOption?.description}
          </DropdownHeader>
        )}
        <DropdownList id={listboxId}>
          {selectOptions.map((option, index) => {
            const { value, label } = option;
            const isSelected = value === selectedOption?.value;
            const isFirstInCategory = (option as any).isFirstInCategory;
            const category = (option as any).category;

            const getCategoryTitle = (cat: string) => {
              switch (cat) {
                case 'base':
                  return 'Base Models';
                case 'finetuned':
                  return 'Fine-tuned Models';
                case 'specialized':
                  return 'Custom Models';
                case 'other':
                  return 'Other Models';
                default:
                  return '';
              }
            };

            const items = [];

            // Add section header if this is the first item in a category
            if (isFirstInCategory) {
              items.push(
                <DropdownSectionTitle key={`section-${category}`}>
                  {getCategoryTitle(category)}
                </DropdownSectionTitle>
              );
            }

            // Add the actual dropdown item
            items.push(
              <DropdownItem
                key={value}
                id={getOptionId(index)}
                // the shared header always shows the ACTIVE option's
                // description — referencing it from every option makes screen
                // readers announce the description when the active descendant
                // changes (keyboard users see it; SR users must hear it)
                aria-describedby={
                  showOptionDescription ? descriptionHeaderId : undefined
                }
                value={value}
                label={!isValidElement(label) ? `${label}` : undefined}
                badgeLabel={isSelected ? selectedLabel : undefined}
                onClick={handleClickItem(option)}
                onMouseOver={() => setActiveIndex(index)}
                onMouseLeave={() => setActiveIndex(-1)}
                isSelected={isSelected}
                isActive={index === activeIndex}
              >
                {isValidElement(label) && label}
              </DropdownItem>
            );

            return items;
          })}
        </DropdownList>
      </DropdownLayer>
    </>
  );
};

export default Select;
